import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, Row, Col, theme, App, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useEmbed } from '@aseller/embed-sdk/react';
import { getAllowedOrigins } from '../utils/allowedOrigins';
import { useTranslation } from 'react-i18next';
import { setLocale } from '../i18n';
import { useHubPlatformAuth } from '../hooks/useHubPlatformAuth';
import Calculator from './Calculator';
import type { CalculatorData } from './Calculator';
import CalculatorSummary from './CalculatorSummary';
import BsrChart from './BsrChart';
import SalesPlan from './SalesPlan';
import type { SalesPlanData } from './SalesPlan';
import ForecastAmazonUS from './ForecastAmazonUS';
import type { ForecastSummaryData } from './ForecastAmazonUS';
import Summary from './Summary';
import Budget from './Budget';
import Notes from './Notes';
import CompetitorAnalysis from './CompetitorAnalysis';
import Header from './Header';
import { loadSavedResult } from '../api/client';
import { mapSalesPlan, mapCalcData } from '../utils/resultMapping';
import { sumBudget, computeCogs, computeFullUnitCost } from '../utils/budget';

const EmbedView: React.FC = () => {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { t } = useTranslation();
  // Origin'ы Hub'а зашиты в getAllowedOrigins (env их лишь расширяет): раньше здесь
  // читался только VITE_ALLOWED_ORIGINS, который в собранном бандле оказывался пустым,
  // и SDK отбрасывал INIT от Hub'а — плагин навсегда висел на «Подключение к Hub».
  const embed = useEmbed({
    toolName: 'idea-calc',
    allowedOrigins: getAllowedOrigins(),
  });

  // CU-869e4mvzp: asellerToken из INIT → платформенный access → Bearer.
  // Рендер не блокируем: ядро калькулятора публично, а персональный слой
  // (сохранение/история) оживает, как только обмен завершится.
  useHubPlatformAuth();

  // CU-869dne01h: ждём embed.ready перед применением locale от SDK.
  // Раньше SDK инициализировался с дефолтом 'en' (truthy) → setLocale('en')
  // срабатывал на первом рендере ДО INIT от Hub → затирал сохранённую
  // пользователем локаль в localStorage. Старый Hub без поддержки locale →
  // юзер навсегда оказывался на 'en' с flash-of-English.
  useEffect(() => {
    if (embed.ready && embed.locale) setLocale(embed.locale);
  }, [embed.ready, embed.locale]);
  const [calculatorData, setCalculatorData] = useState<CalculatorData | undefined>();
  const [salesPlanData, setSalesPlanData] = useState<SalesPlanData | undefined>();
  const [forecastSummary, setForecastSummary] = useState<ForecastSummaryData | null>(null);
  const [asin, setAsin] = useState('');
  const [competitorAsins, setCompetitorAsins] = useState<string[]>([]);
  const [keepaLoaded, setKeepaLoaded] = useState(false);
  const [loadedResult, setLoadedResult] = useState<Record<string, unknown> | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [budgetData, setBudgetData] = useState<any>(undefined);
  const [notesData, setNotesData] = useState<string>('');

  // Auto-resize iframe — observe #root (React mount point).
  // We pin antd popups into #root via ConfigProvider.getPopupContainer in
  // App.tsx; combined with overflow-x:hidden below, hovering tooltips at
  // the right edge can't cause horizontal scroll bars that would reflow
  // content and trigger spurious RESIZE events.
  useEffect(() => {
    if (!embed.embedded) return;
    // Prevent right-edge tooltips from inducing a scrollbar that changes layout
    const prevOverflowX = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    const root = document.getElementById('root') ?? document.body;
    let lastSent = 0;
    let timer: number | null = null;
    const schedule = (height: number) => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        if (Math.abs(height - lastSent) <= 16) return;
        lastSent = height;
        embed.sendResize(height);
      }, 100);
    };
    const observer = new ResizeObserver(([entry]) => {
      schedule(Math.ceil(entry.contentRect.height));
    });
    observer.observe(root);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      observer.disconnect();
      document.body.style.overflowX = prevOverflowX;
    };
  }, [embed.embedded, embed.sendResize]);

  // Send output data to parent when forecast changes
  useEffect(() => {
    if (forecastSummary) {
      embed.sendOutput({ forecastSummary, calculatorData });
    }
  }, [forecastSummary, calculatorData]);

  const handleCalcChange = useCallback((data: CalculatorData) => {
    setCalculatorData(data);
    if (data.asin) setAsin(data.asin);
    else setAsin('');
    if (data.competitorAsins?.length) setCompetitorAsins(data.competitorAsins);
    setKeepaLoaded(!!data.keepaLoaded);
  }, []);

  const handleLoadResult = useCallback(async (fileName: string) => {
    try {
      const data = await loadSavedResult(fileName + '.json');
      setLoadedResult(data);
      setLoadKey((k) => k + 1);
      setLoadedFileName(fileName);
    } catch {
      message.error(t('idea_calc.messages.load_calc_error'));
    }
  }, []);

  const calcInitial = useMemo(
    () => loadedResult ? mapCalcData(loadedResult.calculation_data as Record<string, unknown>) : undefined,
    [loadedResult],
  );
  const budgetInitial = useMemo(() => loadedResult?.budget_data as any, [loadedResult]);
  const salesPlanInitial = useMemo(() => mapSalesPlan(loadedResult?.sales_plan), [loadedResult]);
  const notesInitial = useMemo(() => loadedResult?.notes as string | undefined, [loadedResult]);

  // Calculate total budget from budget categories (matching DashboardView logic)
  const totalLaunchBudget = useMemo(() => {
    if (!budgetData) return 15000;
    const cogs = calculatorData ? computeCogs(calculatorData) : 5;
    const fullUnitCost = calculatorData ? computeFullUnitCost(calculatorData) : 0;
    return sumBudget(budgetData, cogs, fullUnitCost);
  }, [budgetData, calculatorData]);

  // Готовность = только SDK-handshake (aseller — AUTH, hub — INIT). Keycloak-токен
  // больше не требуется (CU-869dt3dz6, вариант 1): ядро калькулятора публично,
  // персональный слой отдаёт 401 до подключения платформенной авторизации.
  const isReady = embed.mode === 'hub' ? embed.ready : embed.isAuthenticated;
  if (!isReady) {
    // In Hub embed mode iframe auto-resizes to body height; using `100vh` here
    // would emit a RESIZE that grows the iframe to full viewport, pushing the
    // spinner off-screen. Use a small fixed height so the spinner stays at
    // the top of the iframe area.
    const wrapperHeight = embed.embedded ? 320 : '100vh';
    return (
      <div style={{ minHeight: wrapperHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        {embed.authTimedOut ? (
          <Typography.Text type="secondary">{t('idea_calc.embed.auth_required')}</Typography.Text>
        ) : (
          <>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} />} />
            <Typography.Text type="secondary">
              {embed.mode === 'hub' ? t('idea_calc.embed.connecting_hub') : t('idea_calc.embed.waiting_auth')}
            </Typography.Text>
          </>
        )}
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Header calculatorData={calculatorData} salesPlanData={salesPlanData} budgetData={budgetData} notesData={notesData} onLoadResult={handleLoadResult} loadedFileName={loadedFileName} />
      <Layout.Content
        style={{
          maxWidth: 1720,
          width: '100%',
          margin: '0 auto',
          padding: token.paddingSM,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div id="calculator">
            <Row gutter={[12, 12]} align="stretch">
              <Col xs={24} lg={6} style={{ display: 'flex' }}>
                <Calculator key={`calc-${loadKey}`} onDataChange={handleCalcChange} initialData={calcInitial} />
              </Col>
              <Col xs={24} lg={10} style={{ display: 'flex' }}>
                <div id="budget" style={{ display: 'flex', width: '100%' }}>
                  <Budget
                    key={`budget-${loadKey}`}
                    cogs={calculatorData ? computeCogs(calculatorData) : undefined}
                    fullUnitCost={calculatorData ? computeFullUnitCost(calculatorData) : undefined}
                    initialData={budgetInitial}
                    onDataChange={setBudgetData}
                  />
                </div>
              </Col>
              <Col xs={24} lg={8} style={{ display: 'flex' }}>
                <CalculatorSummary data={calculatorData} launchBudget={totalLaunchBudget} couponPromoEnabled={calculatorData?.couponPromoEnabled} />
              </Col>
            </Row>
          </div>
          {asin && keepaLoaded && <BsrChart asin={asin} />}
          {(asin || competitorAsins.length > 0) && (
            <div id="competitor-analysis">
              <CompetitorAnalysis
                mainAsin={asin}
                competitorAsins={competitorAsins}
                ourPrice={calculatorData?.sellingPrice}
                ourCogs={calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : 0}
              />
            </div>
          )}
          <div id="sales-plan">
            <SalesPlan
              key={`sp-${loadKey}`}
              onDataChange={setSalesPlanData}
              asin={asin}
              competitorAsins={competitorAsins}
              purchasePrice={calculatorData?.purchasePrice}
              initialData={salesPlanInitial}
            />
          </div>
          <div id="forecast">
            <ForecastAmazonUS
              calculatorData={calculatorData}
              salesPlanData={salesPlanData}
              launchBudget={totalLaunchBudget}
              onSummaryChange={setForecastSummary}
            />
          </div>
          <div id="summary">
            <Summary forecastSummary={forecastSummary} />
          </div>
          <div id="notes">
            <Notes key={`notes-${loadKey}`} initialNotes={notesInitial} onDataChange={setNotesData} />
          </div>
        </div>
      </Layout.Content>
{/* History is now accessible via Header */}
    </Layout>
  );
};

export default EmbedView;
